import React, { ComponentType, FunctionComponent, Suspense, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch } from 'react-router-dom'

import { registerResource, unregisterResource } from '../actions'
import WithPermissions from '../auth/WithPermissions'
import { ReduxState, ResourceComponentFactory, ResourceMatch, ResourceOptions, ResourceProps } from '../types'

const defaultOptions: ResourceOptions = {}

const ResourceRegister: FunctionComponent<ResourceProps> = ({
  name,
  list,
  create,
  edit,
  show,
  icon,
  options = defaultOptions,
}) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(
      registerResource({
        name,
        options,
        hasList: !!list,
        hasEdit: !!edit,
        hasShow: !!show,
        hasCreate: !!create,
        icon,
      })
    )
    return () => {
      dispatch(unregisterResource(name))
    }
  }, [dispatch, name, create, edit, icon, list, show, options])
  return null
}

const ResourceRoutes: FunctionComponent<ResourceProps> = ({
  name,
  match,
  list,
  create,
  edit,
  show,
  options = defaultOptions,
}) => {
  const isRegistered = useSelector((state: ReduxState) => !!state.admin.resources[name])

  const basePath = options.basePath || match?.path || ''

  // match tends to change even on the same route ; using memo to avoid an extra render
  return useMemo(() => {
    // if the registration hasn't finished, no need to render
    if (!isRegistered) {
      return null
    }
    const props = {
      resource: name,
      options,
      hasList: !!list,
      hasEdit: !!edit,
      hasShow: !!show,
      hasCreate: !!create,
    }

    const realCreate = isImporter(create) ? lazify(create as ResourceComponentFactory) : (create as ComponentType<any>)
    const realShow = isImporter(show) ? lazify(show as ResourceComponentFactory) : (show as ComponentType<any>)
    const realEdit = isImporter(edit) ? lazify(edit as ResourceComponentFactory) : (edit as ComponentType<any>)
    const realList = isImporter(list) ? lazify(list as ResourceComponentFactory) : (list as ComponentType<any>)

    return (
      <Switch>
        {create && (
          <Route
            path={`${basePath}/create`}
            render={(routeProps) => (
              <WithPermissions component={realCreate} basePath={basePath} {...routeProps} {...props} />
            )}
          />
        )}
        {show && (
          <Route
            path={`${basePath}/:id/show`}
            render={(routeProps) => (
              <WithPermissions
                component={realShow}
                basePath={basePath}
                id={getIdFromParams(routeProps)}
                {...routeProps}
                {...props}
              />
            )}
          />
        )}
        {edit && (
          <Route
            path={`${basePath}/:id`}
            render={(routeProps) => (
              <WithPermissions
                component={realEdit}
                basePath={basePath}
                id={getIdFromParams(routeProps)}
                {...routeProps}
                {...props}
              />
            )}
          />
        )}
        {list && (
          <Route
            path={`${basePath}`}
            render={(routeProps) => (
              <WithPermissions component={realList} basePath={basePath} {...routeProps} {...props} />
            )}
          />
        )}
      </Switch>
    )
  }, [basePath, name, create, edit, list, show, options, isRegistered])
}

const getIdFromParams = (routeParams: { match: ResourceMatch }) => {
  return routeParams.match.params.id ? decodeURIComponent(routeParams.match.params.id) : undefined
}

const Resource: FunctionComponent<ResourceProps> = ({ intent = 'route', ...props }) =>
  intent === 'registration' ? <ResourceRegister {...props} /> : <ResourceRoutes {...props} />

export default Resource

const lazify = (importer: ResourceComponentFactory) => {
  const LazyComp = React.lazy(() =>
    importer.lazy().then((module) => (importer.component ? { default: module[importer.component] } : module))
  )
  return (props) => {
    return (
      <Suspense fallback={<></>}>
        <LazyComp {...props} />
      </Suspense>
    )
  }
}

function isImporter(value) {
  return value != null && typeof value === 'object' && typeof value['lazy'] === 'function'
}
